import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  LinearProgress,
} from "@mui/material";
import { FaSave, FaTimes, FaInfoCircle } from "react-icons/fa";

const useRootCauseForm = (initialData) => {
  const [formData, setFormData] = useState({
    cause: initialData?.cause || "",
    action: initialData?.action || "",
    responsible: initialData?.responsible || "",
    initialDate: initialData?.initialDate || "",
    finalDate: initialData?.finalDate || "",
    status: initialData?.status || "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.cause.trim()) newErrors.cause = "Causa é obrigatória";
    if (!formData.action.trim()) newErrors.action = "Ação é obrigatória";
    if (!formData.responsible.trim()) newErrors.responsible = "Responsável é obrigatório";
    if (!formData.initialDate) newErrors.initialDate = "Data de início é obrigatória";
    if (!formData.finalDate) newErrors.finalDate = "Data de término é obrigatória";
    if (!formData.status) newErrors.status = "Estado é obrigatório";
    if (
      formData.initialDate &&
      formData.finalDate &&
      new Date(formData.initialDate) >= new Date(formData.finalDate)
    ) {
      newErrors.finalDate = "Data de término deve ser posterior à data de início";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFormProgress = useCallback(() => {
    const fields = Object.keys(formData);
    const filledFields = fields.filter((f) => formData[f]?.toString().trim());
    return Math.round((filledFields.length / fields.length) * 100);
  }, [formData]);

  return { formData, errors, handleChange, validateForm, getFormProgress, setFormData, setErrors };
};

const CustomTextField = ({ label, field, formData, errors, handleChange, multiline = false, rows = 1, maxLength = 500, placeholder = "", type = "text" }) => {

  const isDateField = type === "date";

  return (
    <TextField
      label={label}
      placeholder={placeholder}
      value={formData[field]}
      onChange={(e) => handleChange(field, e.target.value)}
      multiline={multiline}
      rows={rows}
      fullWidth
      type={type}
      error={!!errors[field]}
      helperText={
        errors[field]
          ? errors[field]
          : !isDateField && `${formData[field]?.length || 0}/${maxLength} caracteres`
      }
      inputProps={{maxLength}}
      InputLabelProps={type === "date" ? {shrink: true} : {}}
      sx={{
        "& .MuiOutlinedInput-root": {
          "&.Mui-focused fieldset": {borderColor: "#059669"},
        },
        "& .MuiInputLabel-root.Mui-focused": {color: "#059669"},
      }}
    />
  );
}

const RootCauseFormDialog = ({ open, onClose, onSave, initialData = null, referenceData }) => {
  const { formData, errors, handleChange, validateForm, getFormProgress, setFormData, setErrors } =
    useRootCauseForm(initialData);

  const statusOptions = [
    { name: "Não iniciado", value: "NOT_STARTED", color: "default" },
    { name: "Em andamento", value: "IN_PROGRESS", color: "primary" },
    { name: "Concluído", value: "COMPLETED", color: "success" },
    { name: "Cancelado", value: "CANCELLED", color: "error" },
    { name: "Em espera", value: "ON_HOLD", color: "warning" },
    { name: "Em revisão", value: "UNDER_REVIEW", color: "secondary" },
  ];

  const handleClose = () => {
    setFormData({
      cause: "",
      action: "",
      responsible: "",
      initialDate: "",
      finalDate: "",
      status: "",
    });
    setErrors({});
    onClose();
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
      handleClose();
    }
  };

  const getSelectedStatusOption = () => statusOptions.find((opt) => opt.value === formData.status);

  const getDateWarning = () => {
    if (!formData.finalDate || formData.status === "COMPLETED") return null;
    const current = new Date().setHours(0, 0, 0, 0);
    const end = new Date(formData.finalDate).setHours(0, 0, 0, 0);
    const oneWeekBefore = end - 7 * 24 * 60 * 60 * 1000;
    if (current > end) return { type: "error", message: "⚠️ Prazo vencido!" };
    if (current >= oneWeekBefore) return { type: "warning", message: "⏰ Prazo próximo do vencimento!" };
    return null;
  };

  const dateWarning = getDateWarning();

  const actions = [
    {
      label: "Cancelar",
      icon: <FaTimes />,
      onClick: handleClose,
      variant: "outlined",
      sx: { borderColor: "#d1d5db", color: "#6b7280", "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" } },
    },
    {
      label: initialData ? "Atualizar" : "Salvar",
      icon: <FaSave />,
      onClick: handleSave,
      variant: "contained",
      sx: { backgroundColor: "#059669", "&:hover": { backgroundColor: "#047857" }, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)" } }}
    >
      <DialogTitle
        sx={{
          p: 3,
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FaInfoCircle style={{ color: "#059669", fontSize: "24px" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
              {initialData ? "Editar" : "Nova"} Análise de Causa Raiz/Plano de Ação
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Preenchimento completo: {getFormProgress()}%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={getFormProgress()}
            sx={{
              width: 80,
              height: 6,
              borderRadius: 3,
              backgroundColor: "#f0f0f0",
              "& .MuiLinearProgress-bar": { backgroundColor: "#059669" },
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Informações de referência */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <Typography variant="subtitle2" sx={{ color: "#475569", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <FaInfoCircle size={14} /> Informações de Referência
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><Typography variant="body2" color="textSecondary"><strong>Código:</strong> {referenceData?.referenceCode}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2" color="textSecondary"><strong>Parâmetro:</strong> {referenceData?.parameterName}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2" color="textSecondary"><strong>Cidade:</strong> {referenceData?.city}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2" color="textSecondary"><strong>Tipo de Serviço:</strong> {referenceData?.serviceType}</Typography></Grid>
          </Grid>
        </Box>

        {/* Campos do formulário */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CustomTextField label="Causa Raiz *" field="cause" formData={formData} errors={errors} handleChange={handleChange} multiline rows={3} placeholder="Descreva detalhadamente a causa raiz..." />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField label="Plano de Ação *" field="action" formData={formData} errors={errors} handleChange={handleChange} multiline rows={3} placeholder="Descreva as ações específicas..." />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField label="Responsável *" field="responsible" formData={formData} errors={errors} handleChange={handleChange} maxLength={100} placeholder="Digite o responsável" />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel sx={{ "&.Mui-focused": { color: "#059669" } }}>Estado *</InputLabel>
              <Select
                variant="outlined"
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                label="Estado *"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label={option.name} size="small" color={option.color} variant="outlined" />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.status && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>{errors.status}</Typography>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField label="Data de Início *" field="initialDate" formData={formData} errors={errors} handleChange={handleChange} type="date" />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField label="Data de Término *" field="finalDate" formData={formData} errors={errors} handleChange={handleChange} type="date" />
          </Grid>

          {dateWarning && (
            <Grid item xs={12}>
              <Alert severity={dateWarning.type} sx={{ borderRadius: "8px", "& .MuiAlert-message": { fontWeight: 500 } }}>
                {dateWarning.message}
              </Alert>
            </Grid>
          )}

          {formData.status && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="textSecondary">Estado selecionado:</Typography>
                <Chip label={getSelectedStatusOption()?.name} color={getSelectedStatusOption()?.color} size="small" />
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #f0f0f0", gap: 1 }}>
        {actions.map((btn, idx) => (
          <Button key={idx} onClick={btn.onClick} variant={btn.variant} startIcon={btn.icon} sx={btn.sx}>
            {btn.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default RootCauseFormDialog;
